/**
 * `long_text` — `LongTextValue` (`specs/M2-tier1-column-types.md` §5.4, `[09§A.7 long_text]`).
 *
 * **Unlike `text`, the write form is a nested object**: `{"long_text": {"text": "a\nb"}}`.
 * Newlines are preserved literally, CRLF included, and `toText` never normalises them
 * `[cvs§2 long_text]`. The activity `column_type` is the legacy `long-text` `[vi§126]`.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
/** No cap observed (longest sample ≈ 2 KB); the `text` ceiling is ASSUMED (Q65). */
export const LONG_TEXT_MAX_LENGTH = 65_536;
export const longTextValueSchema = z.looseObject({
    text: z.string().max(LONG_TEXT_MAX_LENGTH),
    changed_at: z.string().optional(),
});
import { longTextFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const longTextSettingsSchema = z.looseObject({
    hide_footer: z.boolean().optional(),
});
function coerce(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string')
        return raw === '' ? null : { text: raw };
    if (typeof raw === 'object') {
        const record = raw;
        const body = record.text;
        if (body === null || body === undefined || body === '')
            return null;
        if (typeof body !== 'string')
            throw new ColumnValueError('long_text', '"text" must be a string');
        if (body.length > LONG_TEXT_MAX_LENGTH) {
            throw new ColumnValueError('long_text', `value exceeds the ${LONG_TEXT_MAX_LENGTH} character capacity`);
        }
        const value = { text: body };
        if (keepVolatile && typeof record.changed_at === 'string')
            value.changed_at = record.changed_at;
        return value;
    }
    throw new ColumnValueError('long_text', `expected an object or string, received ${typeof raw}`);
}
export const longTextColumn = defineColumnType({
    type: 'long_text',
    title: 'Long Text',
    description: 'Multi-line free text; newlines are preserved verbatim.',
    apiFragmentName: 'LongTextValue',
    legacyTypeName: 'long-text',
    idPrefix: 'long_text',
    valueSchema: longTextValueSchema,
    settingsSchema: longTextSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: longTextSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: longTextFilterConditions,
    filterOperators: operatorsOf(longTextFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return coerce(raw, false);
    },
    parseSimpleValue(raw) {
        return coerce(raw, false);
    },
    toText(value) {
        return value?.text ?? '';
    },
    toSortKeys(value) {
        return { text: textShadow(value?.text), number: null, date: null };
    },
    isEqual(a, b) {
        return (a?.text ?? null) === (b?.text ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { text: value.text };
    },
    fromMondayValue(raw) {
        return coerce(raw, true);
    },
    toMondayValue(value) {
        return value === null ? null : { ...value };
    },
    toSimpleValue(value) {
        return value?.text ?? '';
    },
    fromMondaySettings(raw) {
        return longTextSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        // Only `updated_at` beyond the interface `[cvs§2 long_text]`.
        return { updated_at: toFragmentTimestamp(value?.changed_at) };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map